import store from "@/store";
import { SpecimenNumbersEnum } from "./enums";
import { toLetters } from "./helpers";
import moment from "moment";

export function printCassettesFileDrop(cassettes, trayName) {
  const fileDropJSON = store.state.labSettings.FileDropPrintingConfiguration;
  // Default File Drop URL for running locally is http://localhost:18877/api/PrintMate
  let {
    url,
    printerName,
    defaultTray,
    printProcedure1,
    printProcedure2,
    priorities,
    protocolPrintId,
    protocolPrintText,
    printProtocols,
    cassetteStringTemplate
    // denoteSingleSpecimen,
    // denoteSingleBlock
  } = JSON.parse(fileDropJSON);
  if (!cassettes.length) {
    return;
  }
  if (!url) {
    throw { message: "This lab has not set up a File Drop URL. Cassette printing failed." };
  }
  if (protocolPrintId && !printProtocols?.length) {
    printProtocols = [{ protocolPrintId, protocolPrintText }];
  }
  let items = [];
  for (const cassette of cassettes) {
    const {
      caseNumber,
      specimenOrder,
      blockNum,
      cassetteTrayName,
      specimenNumbering,
      patientFirstName,
      patientLastName,
      patientDOB,
      patientMRN,
      collectedOn,
      specimensPerCase,
      blocksPerSpecimen,
      procedureCodes,
      priority,
      protocolId
    } = cassette;
    // Hide specimen if lab setting is on and case only has one specimen
    const hideSpecimenOnLabel =
      store.state.labSettings.FileDropPrintingSuppressSingleSpecimen && specimensPerCase === 1;
    // Hide block if lab setting is on and specimen only has one block
    const hideBlockOnLabel =
      store.state.labSettings.FileDropPrintingSuppressSingleBlock && blocksPerSpecimen === 1;
    const isSingleSpecimen = specimensPerCase === 1 ? "Y" : "N";
    const isSingleBlock = blocksPerSpecimen === 1 ? "Y" : "N";
    let procedure1 = "";
    let procedure2 = "";
    let printPriority = "";
    let printProtocol = "";
    if (procedureCodes.includes(printProcedure1)) {
      procedure1 = printProcedure1;
    }
    if (procedureCodes.includes(printProcedure2)) {
      procedure2 = printProcedure2;
    }
    if (priorities?.length && priorities.includes(priority)) {
      printPriority = priority;
    }
    const protocolToPrint = printProtocols.find(e => e.protocolPrintId === protocolId);
    if (protocolToPrint) {
      printProtocol = protocolToPrint.protocolPrintText;
    }
    const caseNumberRegex =
      /(?<prefix>[A-Z]{1,6})(?<year>((19)|(20))?[0-9]{2})-?0{0,6}(?<caseNumSeq>[1-9][0-9]{0,6})/i;
    const {
      groups: { prefix, year, caseNumSeq }
    } = caseNumberRegex.exec(caseNumber);
    const shortYear = year.slice(-2);
    const blockNumDisplay =
      specimenNumbering === SpecimenNumbersEnum.Numbers ? toLetters(blockNum) : blockNum + "";
    const pipeDelimitedConcat =
      prefix + shortYear + caseNumSeq + "|" + specimenOrder + "|" + blockNumDisplay;
    const printItem = {
      CasePrefixAndYear: prefix + shortYear,
      CaseNumSeq: caseNumSeq,
      SpecimenOrder: hideSpecimenOnLabel ? "" : specimenOrder + "",
      CassetteId: hideBlockOnLabel ? "" : blockNumDisplay,
      PipeDelimitedConcat: pipeDelimitedConcat,
      PatientLastName: patientLastName,
      PatientFirstName: patientFirstName,
      patientFirstInitial: patientFirstName ? patientFirstName[0] : "",
      patientLastInitial: patientLastName ? patientLastName[0] : "",
      DOB: formatDate(patientDOB),
      MRN: patientMRN,
      CollectedOn: formatDate(collectedOn),
      TrayName: trayName || cassetteTrayName || defaultTray,
      procedure1,
      procedure2,
      priority: printPriority,
      protocol: printProtocol,
      denoteSingleSpecimen: isSingleSpecimen,
      denoteSingleBlock: isSingleBlock
    };
    if (cassetteStringTemplate) {
      items.push({ stringToPrint: replaceTokens(printItem, cassetteStringTemplate) });
    } else {
      items.push(printItem);
    }
  }
  const payloadObject = {
    PrinterName: printerName,
    Items: items
  };
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payloadObject)
  });
}

export function printSlidesFileDrop(slides, trayName) {
  const fileDropJSON = store.state.labSettings.FileDropPrintingConfiguration;
  // Default File Drop URL for running locally is http://localhost:18877/api/PrintMate
  let {
    url,
    slidePrinterName,
    defaultSlideTray,
    slideStringTemplate
    // denoteSingleSpecimen,
    // denoteSingleBlock
  } = JSON.parse(fileDropJSON);
  if (!slides.length) {
    return;
  }
  if (!url) {
    throw { message: "This lab has not set up a File Drop URL. Cassette printing failed." };
  }
  let items = [];
  for (const slide of slides) {
    const {
      caseNumber,
      specimenOrder,
      blockNum,
      slideLabelNumber,
      slideLabelDescription,
      slideTrayName,
      site,
      specimenNumbering,
      patientFirstName,
      patientLastName,
      patientMRN,
      providerFirstName,
      providerLastName,
      specimensPerCase,
      blocksPerSpecimen,
      hx,
      collectedOn
    } = slide;
    // Hide specimen if lab setting is on and case only has one specimen
    const hideSpecimenOnLabel =
      store.state.labSettings.FileDropPrintingSuppressSingleSpecimen && specimensPerCase === 1;
    // Hide block if lab setting is on and specimen only has one block
    const hideBlockOnLabel =
      store.state.labSettings.FileDropPrintingSuppressSingleBlock && blocksPerSpecimen === 1;
    const isSingleSpecimen = specimensPerCase === 1 ? "Y" : "N";
    const isSingleBlock = blocksPerSpecimen === 1 ? "Y" : "N";
    const caseNumberRegex =
      /(?<prefix>[A-Z]{1,6})(?<year>((19)|(20))?[0-9]{2})-?0{0,6}(?<caseNumSeq>[1-9][0-9]{0,6})/i;
    const {
      groups: { prefix, year, caseNumSeq }
    } = caseNumberRegex.exec(caseNumber);
    const shortYear = year.slice(-2);
    const blockNumDisplay =
      specimenNumbering === SpecimenNumbersEnum.Numbers ? toLetters(blockNum) : blockNum + "";
    const pipeDelimitedConcat =
      prefix + shortYear + caseNumSeq + "|" + specimenOrder + "|" + blockNumDisplay;
    const printItem = {
      CasePrefixAndYear: prefix + shortYear,
      CaseNumSeq: caseNumSeq,
      SpecimenOrder: hideSpecimenOnLabel ? "" : specimenOrder + "",
      CassetteId: hideBlockOnLabel ? "" : blockNumDisplay,
      SlideNumber: slideLabelNumber,
      SlideLabelDescription: slideLabelDescription,
      Site: site,
      PipeDelimitedConcat: pipeDelimitedConcat,
      PatientLastName: patientLastName,
      PatientFirstName: patientFirstName,
      patientFirstInitial: patientFirstName ? patientFirstName[0] : "",
      patientLastInitial: patientLastName ? patientLastName[0] : "",
      MRN: patientMRN,
      ProviderFirstName: providerFirstName,
      ProviderLastName: providerLastName,
      HasHistory: hx ? "Y" : "N",
      CollectedOn: formatDate(collectedOn),
      TrayName: trayName || slideTrayName || defaultSlideTray,
      denoteSingleSpecimen: isSingleSpecimen,
      denoteSingleBlock: isSingleBlock
    };
    if (slideStringTemplate) {
      items.push({ stringToPrint: replaceTokens(printItem, slideStringTemplate) });
    } else {
      items.push(printItem);
    }
  }
  const payloadObject = {
    PrinterName: slidePrinterName,
    Items: items
  };
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payloadObject)
  });
}

export function replaceTokens(data, template) {
  let returnText = template;
  const matches = [...template.matchAll(/{\w+}/gi)];
  for (const [match] of matches) {
    const field = match.replace("{", "").replace("}", "");
    if (Object.keys(data).includes(field)) {
      const regex = new RegExp(match, "ig");
      const key = Object.keys(data).find(e => e.toLowerCase() === field.toLowerCase());
      returnText = returnText.replaceAll(regex, data[key] || "");
    }
  }
  return returnText;
}

export function formatDate(date) {
  if (date) {
    return moment(date).format("M/D/YYYY");
  }
  return "";
}
